<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount, nextTick, computed } from 'vue';
import { useRouter,useRoute } from 'vue-router';
import { SvgPlusOutlined, SvgSquareTransferOutline } from '@repo/icons';
import RecentProjects from './recent-projects.vue';
import FabricList from './fabric-list.vue';
import { useRecentProjectListStore } from '@/store/recent-project-list.ts';
import { useRecentFabricListStore } from '@/store/recent-fabric-list.ts';
import { getFabricStyles } from '@/api/cloud-function';
import type { Project } from '@/api';
import { joinUrl } from '@repo/utils';
import { getAppConfig } from '@/utils/env';
import type { Relation, FabricShareInfo } from '@repo/style3d';
import axios, { type AxiosResponse } from 'axios';
import type { FabricStore } from '@/api';
import { getValidImageBase64 } from '@/utils';


const { OSS_BASE_URL, INDIVIDUAL_SHARES, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const projectRootPath = `${OSS_BASE_URL}${INDIVIDUAL_SHARES}`;
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const recentProjectStore = useRecentProjectListStore();
const recentFabricStore = useRecentFabricListStore();

type Mode = 'project' | 'fabric' | 'linkedProject' | 'linkedFabric';
const mode = ref<Mode>(localStorage.getItem('recentListMode') as Mode || 'project');

const router = useRouter();
const onClickCloud = async () => {
  await router.push({ name: 'cloud' });
};

// 正确获取路由实例（关键：params 在 route.params，query 在 route.query）
const route = useRoute();
// use computed so watchers react to route changes
const id = computed(() => (route.params.id as string) || '');
const scoId = computed(() => (route.query.scoId as string) || '');

// 响应式状态
const linkedStyles = ref<Project[]>([]);
const loading = ref<boolean>(false);
const error = ref<string>('');

const linkedFabrics = ref<FabricStore[]>([]);
const loadingLinkedFabrics = ref<boolean>(false);
const errorOnLinkedFabrics = ref<string>('');

// 通过面料id获取关联款式列表
const fetchLinkedStyles = async (currentFabric: string) => {
  // 边界校验：无参数直接返回
  if (!currentFabric) {
    linkedStyles.value = [];
    error.value = '缺少 fabric 参数';
    return;
  }

  try {
    loading.value = true;
    const result = await getFabricStyles({ fabric: currentFabric });

    const stylePromises =  result.styles.map(async (style) => {
      const rootPath = joinUrl(projectRootPath, style);
      const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(rootPath, 'relation.json'));
      const pictureFileName = jsonResult.data.metadata?.stylePicture || "正面图.png";
      const name = jsonResult.data.metadata?.property[0]?.data.find(item => item.key === 'styleName')?.value || '';
      const primaryThumb = encodeURI(joinUrl(projectRootPath, style, "img", pictureFileName));
      const fallbackThumb = encodeURI(joinUrl(projectRootPath, style, "sco", "thumb.png"));
      const validThumb = await getValidImageBase64(primaryThumb, fallbackThumb, "png");
      const item:Project = ({
        id: style,
        scoId: style,
        thumb: validThumb,
        type: 'cloudknit',
        name: name
      });
      return item;
    });

    linkedStyles.value = await Promise.all(stylePromises);

    error.value = '';
  } catch (err) {
    error.value = (err as Error).message;
    linkedStyles.value = [];
  } finally {
    loading.value = false;
  }
};

async function getFabricsFromStyle(currentStyle: string) {
    const rootPath = joinUrl(projectRootPath, currentStyle);
    const jsonResult: AxiosResponse<Relation> = await axios.get(joinUrl(rootPath, 'relation.json'));

    const splitToFabricId = function (str: string): string  {
      const prefix:string = "https://oss.cloudknit.cn/fabric/";
      const id:string = str.startsWith(prefix) ? str.slice(prefix.length) : str;
      return id;
    };
    
    const result = jsonResult.data.metadata?.fabricLinks?.map(fabricLink => {
        return splitToFabricId(fabricLink);
      })||[];
    return result;
}

// 通过款式id获取关联面料列表
const fetchLinkedFabrics = async (currentStyle: string) => {
  // 边界校验：无参数直接返回
  if (!currentStyle) {
    linkedFabrics.value = [];
    errorOnLinkedFabrics.value = '缺少 style 参数';
    return;
  }

  try {
    loadingLinkedFabrics.value = true;
    const result = await getFabricsFromStyle( currentStyle );
    const fabricPromises =  result.map(async (fabric) => {
      const fabricThumb = joinUrl(fabricRootPath, fabric,"fabric", "thumbnail.jpg");

      const fabricRoot = joinUrl(fabricRootPath, fabric);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      const fabricShareInfo = infoResult.data;
      const item: FabricStore = ({
        id: fabric,
        thumb: fabricThumb,
        info:fabricShareInfo.metaData.info,
        fabricId: fabric,
        resourceType:'cloudknit',
        properties: fabricShareInfo.metaData.property
      });
      return item;
    });

    linkedFabrics.value = await Promise.all(fabricPromises);
    errorOnLinkedFabrics.value = '';
  } catch (err) {
    errorOnLinkedFabrics.value = (err as Error).message;
    linkedFabrics.value = [];
  } finally {
    loadingLinkedFabrics.value = false;
  }
};

// watch 监听 fabric 路由参数变化
watch(
  id,
  (newFabric, oldFabric) => {
    if (newFabric === oldFabric) return;
    fetchLinkedStyles(newFabric as string);
  },
  { immediate: true }
);

// watch 监听 fabric 路由参数变化
watch(
  scoId,
  (newStyle, oldStyle) => {
    if (newStyle === oldStyle) return;
    fetchLinkedFabrics(newStyle as string);
  },
  { immediate: true }
);

// const toggle = () => {
//   if (mode.value === 'project') {
//     mode.value = 'linkedProject';
//   }
//   else if (mode.value === 'linkedProject') {
//     mode.value = 'fabric';
//   }
//   else{
//     mode.value = 'project';
//   }
//   localStorage.setItem('recentListMode', mode.value);
// };

// dropdown 状态与点击外部关闭逻辑
const dropdownVisible = ref(false);
const rootRef = ref<HTMLElement | null>(null);
const iconButtonRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);

const dropdownStyle = ref<Record<string, string>>({ position: 'absolute' });

const computeDropdownPosition = () => {
  const btn = iconButtonRef.value;
  const dd = dropdownRef.value;
  if (!btn) return;
  const btnRect = btn.getBoundingClientRect();
  const gap = 8;
  // get dropdown size if available, otherwise assume min width and a reasonable height
  const ddRect = dd ? dd.getBoundingClientRect() : { width: 140, height: 200 } as DOMRect;
  const dropdownWidth = Math.max(140, ddRect.width || 140);

  // compute horizontal position: try to align right edge with button's right, but keep inside viewport
  let left = btnRect.right - dropdownWidth;
  left = Math.min(left, window.innerWidth - dropdownWidth - 8);
  left = Math.max(8, left);

  // compute vertical position: prefer below, but flip above if not enough space
  const spaceBelow = window.innerHeight - btnRect.bottom;
  const spaceAbove = btnRect.top;
  let top: number;
  if (spaceBelow >= ddRect.height + gap) {
    top = btnRect.bottom + gap; // enough room below
  } else if (spaceAbove >= ddRect.height + gap) {
    top = btnRect.top - ddRect.height - gap; // place above
  } else {
    // neither side has enough full space: pick side with more room and clamp inside viewport
    if (spaceBelow >= spaceAbove) {
      top = Math.min(btnRect.bottom + gap, window.innerHeight - ddRect.height - 8);
    } else {
      top = Math.max(8, btnRect.top - ddRect.height - gap);
    }
  }

  dropdownStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    zIndex: '10000'
  };
};

const toggleDropdown = async (evt?: Event) => {
  if (evt) evt.stopPropagation();
  dropdownVisible.value = !dropdownVisible.value;
  if (dropdownVisible.value) {
    await nextTick();
    computeDropdownPosition();
  }
};

const selectMode = (m: Mode) => {
  mode.value = m;
  localStorage.setItem('recentListMode', mode.value);
  dropdownVisible.value = false;
};

const handleDocumentClick = (e: MouseEvent) => {
  const root = rootRef.value;
  const dd = dropdownRef.value;
  if (root && root.contains(e.target as Node)) return;
  if (dd && dd.contains(e.target as Node)) return;
  dropdownVisible.value = false;
};

const handleWindowChange = () => {
  if (dropdownVisible.value) computeDropdownPosition();
};

onMounted(() => {
  document.addEventListener('click', handleDocumentClick);
  window.addEventListener('resize', handleWindowChange);
  window.addEventListener('scroll', handleWindowChange, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick);
  window.removeEventListener('resize', handleWindowChange);
  window.removeEventListener('scroll', handleWindowChange, true);
});

</script>
<template>
  <div :class="$style.shareListContainer">
    <div :class="$style.extra" id="cloud-button">
      <SvgPlusOutlined style="width:30px;height:30px;color: #eee;" @click="onClickCloud" />
    </div>
    <div :class="$style.recentList">
      <RecentProjects
        :projects="linkedStyles"
        v-if="mode==='linkedProject'"
      />
      <RecentProjects
        :projects="recentProjectStore.recentProjects"
        v-else-if="mode==='project'"
      />
      <FabricList
        :fabrics="linkedFabrics"
        v-else-if="mode==='linkedFabric'"
      />
      <FabricList
        :fabrics="recentFabricStore.recentFabrics"
        v-else
      />
    </div>
    <div :class="$style.extra" ref="rootRef">
      <div @click.stop="toggleDropdown" :class="$style.iconButton" ref="iconButtonRef">
        <SvgSquareTransferOutline style="width:30px;height:30px;color: #eee;" />
      </div>
      <teleport to="body">
        <div v-if="dropdownVisible" :class="$style.dropdown" :style="dropdownStyle" ref="dropdownRef">
            <div :class="[$style.item, mode === 'project' ? $style.active : '']" @click="selectMode('project')">{{ $t('list-switch.recent-style') }}</div>
            <div :class="[$style.item, mode === 'linkedProject' ? $style.active : '']" @click="selectMode('linkedProject')">{{ $t('list-switch.link-style') }}</div>
            <div :class="[$style.item, mode === 'fabric' ? $style.active : '']" @click="selectMode('fabric')">{{ $t('list-switch.recent-swatch') }}</div>
            <div :class="[$style.item, mode === 'linkedFabric' ? $style.active : '']" @click="selectMode('linkedFabric')">{{ $t('list-switch.link-swatch') }}</div>
        </div>
      </teleport>
    </div>
  </div>
</template>
<style module>
.shareListContainer {
  padding: 0px 0px 0px;
  display: flex;
}

.recentList {
  flex: 1;
  display: flex;
  overflow: auto;
}

.extra {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
}

/* 新增：下拉样式 */
.iconButton {
  cursor: pointer;
  position: relative;
}

.dropdown {
  background: #222;
  color: #fff;
  border-radius: 4px;
  box-shadow: 0 6px 12px rgba(0,0,0,0.2);
  min-width: 140px;
  overflow: hidden;
}

.item {
  padding: 8px 12px;
  cursor: pointer;
  white-space: nowrap;
}

.item:hover {
  background: rgba(255,255,255,0.04);
}

.active {
  background: rgba(255,255,255,0.02);
  font-weight: 600;
}
</style>
